import React from "react";
import { motion } from "framer-motion";
import {
  MessageSquareText,
  Send,
  Smartphone,
  ShieldCheck,
} from "lucide-react";

export default function WorkflowSection() {
  return (
    <section
      className="
        relative
        overflow-hidden
        theme-bg-primary
        py-12
        font-saira
        lg:py-16
      "
    >
      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          absolute
          left-1/2
          top-1/2
          h-[450px]
          w-[450px]
          -translate-x-1/2
          -translate-y-1/2
          rounded-full
          bg-orange-500/5
          blur-[170px]
        "
      />

      <div
        className="
          absolute
          right-0
          top-16
          h-64
          w-64
          rounded-full
          bg-orange-500/8
          blur-[130px]
        "
      />

      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* =====================================================
            HEADING
        ====================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 35,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.7,
          }}
          className="mx-auto max-w-5xl text-center"
        >
          <span
            className="
              text-xs
              font-semibold
              uppercase
              tracking-[4px]
              brand-color
              sm:text-sm
            "
          >
            OTP Workflow
          </span>

          <h2
            className="
              mt-4
              text-3xl
              font-bold
              leading-[1.12]
              theme-text-primary
              lg:text-[46px]
            "
          >
            Secure OTP Verification In Four
            <br />

            <span className="brand-color">
              Simple Steps
            </span>
          </h2>

          <p
            className="
              mx-auto
              mt-5
              max-w-4xl
              text-[15px]
              leading-7
              theme-text-secondary
              lg:text-base
            "
          >
            Every OTP request follows a secure lifecycle—from request
            initiation and encrypted OTP generation to instant delivery and
            secure verification. Built for enterprise applications that demand
            speed, reliability, and maximum protection.
          </p>
        </motion.div>

        {/* =====================================================
            DESKTOP CIRCULAR WORKFLOW
        ====================================================== */}

        <div
          className="
            relative
            mx-auto
            mt-14
            hidden
            h-[500px]
            max-w-[1180px]
            items-center
            justify-center
            lg:flex
          "
        >
          {/* =================================================
              ANIMATED CONNECTORS
          ================================================= */}

          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 1200 700"
            fill="none"
          >
            {/* 1 → 2 */}

            <motion.path
              d="M600 135 C760 135 900 210 965 320"
              stroke="var(--brand-primary)"
              strokeWidth="2.5"
              strokeDasharray="10 10"
              strokeLinecap="round"
              animate={{
                strokeDashoffset: [80, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear",
              }}
            />

            {/* 2 → 3 */}

            <motion.path
              d="M965 380 C900 490 760 565 600 565"
              stroke="var(--brand-primary)"
              strokeWidth="2.5"
              strokeDasharray="10 10"
              strokeLinecap="round"
              animate={{
                strokeDashoffset: [80, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear",
                delay: 0.5,
              }}
            />

            {/* 3 → 4 */}

            <motion.path
              d="M600 565 C440 565 300 490 235 380"
              stroke="var(--brand-primary)"
              strokeWidth="2.5"
              strokeDasharray="10 10"
              strokeLinecap="round"
              animate={{
                strokeDashoffset: [80, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear",
                delay: 1,
              }}
            />

            {/* 4 → 1 */}

            <motion.path
              d="M235 320 C300 210 440 135 600 135"
              stroke="var(--brand-primary)"
              strokeWidth="2.5"
              strokeDasharray="10 10"
              strokeLinecap="round"
              animate={{
                strokeDashoffset: [80, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear",
                delay: 1.5,
              }}
            />
          </svg>

          {/* =================================================
              STEP 1
          ================================================= */}

          <div
            className="
              absolute
              left-1/2
              top-[-10px]
              -translate-x-1/2
            "
          >
            <WorkflowCard
              icon={MessageSquareText}
              step="01"
              title="Request OTP"
              description="Application requests OTP for login, signup, payments, or secure account verification."
            />
          </div>

          {/* =================================================
              STEP 2
          ================================================= */}

          <div
            className="
              absolute
              right-[70px]
              top-1/2
              -translate-y-1/2
            "
          >
            <WorkflowCard
              icon={Send}
              step="02"
              title="Generate OTP"
              description="A secure time-sensitive verification code is generated instantly."
            />
          </div>

          {/* =================================================
              STEP 3
          ================================================= */}

          <div
            className="
              absolute
              bottom-[-10px]
              left-1/2
              -translate-x-1/2
            "
          >
            <WorkflowCard
              icon={Smartphone}
              step="03"
              title="Instant Delivery"
              description="OTP reaches users through fast and reliable SMS delivery routes."
            />
          </div>

          {/* =================================================
              STEP 4
          ================================================= */}

          <div
            className="
              absolute
              left-[70px]
              top-1/2
              -translate-y-1/2
            "
          >
            <WorkflowCard
              icon={ShieldCheck}
              step="04"
              title="Verify User"
              description="The entered OTP is validated securely before granting access."
            />
          </div>
        </div>

        {/* =====================================================
            MOBILE LAYOUT
        ====================================================== */}

        <div className="mt-12 grid gap-5 lg:hidden">
          {[
            {
              icon: MessageSquareText,
              step: "01",
              title: "Request OTP",
              desc: "Application requests OTP for login or signup.",
            },
            {
              icon: Send,
              step: "02",
              title: "Generate OTP",
              desc: "A secure verification code is generated instantly.",
            },
            {
              icon: Smartphone,
              step: "03",
              title: "Instant Delivery",
              desc: "OTP reaches users through reliable SMS routes.",
            },
            {
              icon: ShieldCheck,
              step: "04",
              title: "Verify User",
              desc: "OTP is securely verified before granting access.",
            },
          ].map((item) => (
            <WorkflowCard
              key={item.step}
              icon={item.icon}
              step={item.step}
              title={item.title}
              description={item.desc}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   CARD
============================================================ */

const WorkflowCard = ({
  icon: Icon,
  step,
  title,
  description,
}) => (
  <motion.div
    whileHover={{
      y: -6,
      scale: 1.02,
    }}
    transition={{
      duration: 0.3,
    }}
    className="
      group
      relative
      w-full
      overflow-hidden
      rounded-[24px]
      border
      theme-border
      theme-card
      p-5
      backdrop-blur-xl
      lg:w-[260px]
    "
  >
    {/* ======================================================
        HOVER GLOW
    ====================================================== */}

    <div
      className="
        absolute
        -right-14
        -top-14
        h-28
        w-28
        rounded-full
        bg-orange-500/0
        blur-3xl
        transition-all
        duration-500
        group-hover:bg-orange-500/20
      "
    />

    {/* ======================================================
        ICON
    ====================================================== */}

    <div
      className="
        relative
        flex
        h-12
        w-12
        items-center
        justify-center
        rounded-xl
        border
        border-orange-500/20
        bg-orange-500/10
        brand-color
        transition-all
        duration-300
        group-hover:border-orange-500/40
        group-hover:bg-orange-500/15
        group-hover:shadow-[0_0_25px_rgba(249,115,22,0.18)]
      "
    >
      <Icon
        size={22}
        strokeWidth={1.8}
      />
    </div>

    {/* ======================================================
        STEP
    ====================================================== */}

    <span
      className="
        mt-4
        block
        text-[11px]
        font-bold
        tracking-[0.16em]
        brand-color
      "
      style={{
        opacity: 0.7,
      }}
    >
      STEP {step}
    </span>

    {/* ======================================================
        TITLE
    ====================================================== */}

    <h3
      className="
        mt-2
        text-xl
        font-semibold
        theme-text-primary
      "
    >
      {title}
    </h3>

    {/* ======================================================
        DESCRIPTION
    ====================================================== */}

    <p
      className="
        mt-3
        text-sm
        leading-6
        theme-text-secondary
      "
    >
      {description}
    </p>
  </motion.div>
);
